

import React, { ReactNode, useState, FC } from "react";
import {
    Box,
    makeStyles,
    Theme,
    Typography,
    useTheme,
} from "@material-ui/core";
import Dropzone from "react-dropzone";
import { OpenInBrowser } from "@material-ui/icons";
import { DialogContinue } from "@idha/sanitization-components";
import { useTranslation } from "react-i18next";
export interface DropZoneProps {
    contentComponet?: ReactNode;
    onDropFile: (file: File) => void;
    imageConstrant?: {
        width: number;
        height: number;
    };
}

const useDropZoneStyle = makeStyles((theme: Theme) => ({
    wrapper: {
        display: "flex",
        width: "100%",
        backgroundColor: `${theme.palette.background.paper}`,
    },
    container: {
        border: `2px`,
        borderStyle: "dashed",
        minHeight: "140px",
        display: "flex",
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        textAlign: "center",
    },
    dragicon: {
        transform: `rotate(180deg)`,
        fontSize: "28px",
        color: "rgba(0,0,0,0.54)",
    },
    camera: {
        paddingTop: "4px",
        paddingBottom: "4px",
        minHeight: "240px",
    },
}));
const DropZone: FC<DropZoneProps> = ({ contentComponet, onDropFile }) => {
    const { palette } = useTheme();
    const { container, wrapper, dragicon, camera } = useDropZoneStyle();
    const { t } = useTranslation(["FileUploadv2"]);
    const [error, setError] = useState<
        "file-too-large" | "file-invalid-type"
    >();
    return (
        <Box p={2} className={wrapper}>
            <DialogContinue
                content={
                    error === "file-invalid-type"
                        ? t("bad_content_dialog.file_invalid_type", {
                              fileformat: "png ou jpeg",
                          })
                        : t("bad_content_dialog.file_to_large", {
                              maxSize: "2MB",
                          })
                }
                state={error ? true : false}
                onAgree={() => setError(undefined)}
                title={t("bad_content_dialog.title")}
            />
            {contentComponet ? (
                <Box
                    className={`${container} ${camera}`}
                    color={palette.grey[600]}
                >
                    {contentComponet}
                </Box>
            ) : (
                <Dropzone
                    accept="image/png, image/jpeg"
                    onDropRejected={(fileRejections) => {
                        fileRejections[0].errors[0].code ===
                            "file-invalid-type" &&
                            setError("file-invalid-type");
                        fileRejections[0].errors[0].code === "file-too-large" &&
                            setError("file-too-large");
                    }}
                    onDropAccepted={(files) => {
                        onDropFile(files[0]);
                    }}
                    maxSize={2e6}
                    maxFiles={1}
                >
                    {({ getRootProps, isDragActive, getInputProps }) => {
                        let borderColor = palette.grey[600];
                        if (isDragActive) {
                            borderColor = palette.primary.light;
                        }

                        return (
                            <Box
                                color={borderColor}
                                className={container}
                                {...getRootProps()}
                            >
                                <OpenInBrowser
                                    className={dragicon}
                                    fontSize="inherit"
                                />
                                {error ? (
                                    <></>
                                ) : (
                                    <Typography
                                        align="center"
                                        color={"initial"}
                                        variant={"body1"}
                                    >
                                        {t("dropzone_content")}
                                    </Typography>
                                )}

                                <input {...getInputProps()} />
                            </Box>
                        );
                    }}
                </Dropzone>
            )}
        </Box>
    );
};

export default DropZone;
